import type {
  Options,
  PointOptionsObject,
  SeriesColumnOptions,
  XAxisOptions,
  YAxisOptions,
  ChartOptions as ChartOptionsValue,
} from 'highcharts';
import { Currency as AccountCurrency } from 'api/account';
import { ExportResponse } from 'api/async-export';
import { CreateCustomTableWidgetPayload } from 'api/dashboard/widget';
import { ExportMetaData } from 'api/export/types';
import { Timestamp } from 'api/models';
import { ColumnOrder } from 'components/organisms/grid-custom-column';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { FieldType, SourceType } from 'components/organisms/tasks/fields';
import { Operation } from 'components/organisms/tasks/filters/operations';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';

export enum ReportType {
  Pivot = 'PIVOT',
  Tabular = 'TABULAR',
}

export enum ReportingSourceType {
  Project = 'PROJECT',
  Task = 'TASK',
  User = 'USER',
  TimeTracking = 'TIME_TRACKING',
  Role = 'ROLE',
  Phase = 'PHASE',
  Company = 'COMPANY',
  ProjectFinancialBudget = 'PROJECT_FINANCIAL_BUDGET',
}

export enum AggregateType {
  Average = 'AVG',
  Total = 'SUM',
  Count = 'COUNT',
  None = 'NONE',
}

export enum DateType {
  Day = 'DAY',
  Week = 'WEEK',
  Month = 'MONTH',
  Quarter = 'QUARTER',
  Year = 'YEAR',
}

export enum ZoomLevel {
  Daily = 'DAY',
  Weekly = 'WEEK',
  Monthly = 'MONTH',
  Yearly = 'YEAR',
  Quarterly = 'QUARTER',
}

export enum FieldClass {
  NativeField = 'NATIVE',
  CustomField = 'CUSTOM',
  DefaultField = 'DEFAULT',
}

export enum FieldOptionType {
  SingleSelect = 'SINGLE_SELECT',
  MultiSelect = 'MULTI_SELECT',
  Rating = 'RATING',
  KeyEvents = 'KEY_EVENTS',
  Intervals = 'INTERVALS',
  Formula = 'FORMULA',
}

export enum CustomReportsFieldGroups {
  Task = 'TASK',
  Project = 'PROJECT',
  Role = 'ROLE',
  User = 'USER',
  Partners = 'PARTNERS',
  Allocations = 'ALLOCATIONS',
  TimeTracking = 'TIME_TRACKING',
  Financials = 'FINANCIALS',
  BudgetFinancials = 'BUDGET_FINANCIALS',
  Phase = 'PHASE',
  FormulaFields = 'FORMULA_FIELDS',
  Company = 'COMPANY',
  Budget = 'PROJECT_FINANCIAL_BUDGET',
}

export type CustomReportsFieldGroupsName =
  | 'TASK'
  | 'PROJECT'
  | 'ROLE'
  | 'PARTNERS'
  | 'ALLOCATIONS'
  | 'TIME_TRACKING'
  | 'FINANCIALS'
  | 'BUDGET_FINANCIALS'
  | 'TIME_PERIOD'
  | 'PHASE'
  | 'FORMULA_FIELDS'
  | 'USER'
  | 'COMPANY'
  | 'PROJECT_FINANCIAL_BUDGET';

export enum SortOrderType {
  Asc = 'ASC',
  Desc = 'DESC',
}

export type AgGridSortOrder = 'asc' | 'desc';

export const toAgGridSortOrder = (
  sortOrder: SortOrderType | string,
): AgGridSortOrder => sortOrder.toLowerCase() as AgGridSortOrder;

export type ToggleState = 'expand' | 'collapse' | null;

export enum VisibilityScopeType {
  Everyone = 'everyone',
  Self = 'self',
  Limited = 'limited',
}

export type FieldDetailType = {
  fieldName: string;
  fieldLabel: string;
  fieldDataType: string;
  fieldId?: number;
  fieldType: FieldOptionType;
  customField: boolean;
  metaData: any;
  sourceType: ReportingSourceType;
  showAggregate: boolean;
  operation?: string;
  dateGroup?: string;
  showTimePeriodFilterInUI?: boolean;
};

export type FieldCustomType = {
  [key in CustomReportsFieldGroupsName]: FieldDetailType[];
};

export interface ColumnDetailType extends FieldDetailType {
  id: number;
  type: FieldType;
  showTotal?: boolean;
  sortOrder?: SortOrderType;
}

export interface ColumnType {
  [key: string]: ColumnDetailType[];
}

export type RowDataType = {
  rowId: number;
  fieldName: string;
  isCustomField: boolean;
  operation?: string;
  fieldValue: number | string | Map<string, string>;
  objectType: ReportingSourceType;
  rowKey: string;
};

export type RowDataObjectType = {
  [key: string]: RowDataType;
};

export interface CardSelectType extends FieldDetailType {
  cardId: number;
  isSelected: boolean;
}

export interface CustomCardType extends CardSelectType {
  cardId: number;
  fieldValue?: number | string | Map<string, string>;
}

export interface SortModel {
  sortField?: ColumnDetailType | ColumnDetailType[];
  sortOrder: SortOrderType;
  fieldName?: string;
}

export interface GroupByModel {
  groupByField: FieldDetailType;
  groupKey?: string;
}

export interface TabularReportRequest {
  columns: ColumnDetailType[];
  filter?: SimpleSearchFilter;
  source: ReportingSourceType;
  offset: number;
  limit: number;
  highlightingRules: {
    rules: HighlightingRule[];
  };
  sortModel: SortModel | null;
  groupBy?: GroupByModel;
  startDate: string | null;
  endDate: string | null;
  simpleSearchFilterList?: SimpleSearchFilter[];
  showTableValuesInProjectCurrency?: boolean;
  customReportId?: number;
  viewId?: number;
  reportName?: string;
}

export interface PivotReportRequest {
  columnFields: ColumnDetailType[];
  rowFields: ColumnDetailType[];
  aggregateFields: ColumnDetailType[];
  filter: SimpleSearchFilter;
  simpleSearchFilterList?: SimpleSearchFilter[];
  source: ReportingSourceType;
  reportType: ReportType;
  offset: number;
  limit: number;
  sortModel: SortModel | null;
  highlightingRules: {
    rules: HighlightingRule[];
  };
  customReportId?: number;
  viewId?: number;
  reportName?: string;
}

export type CustomReportExportRequest =
  | TabularReportRequest
  | PivotReportRequest;

export type VisibilityConfig = {
  scopeType: VisibilityScopeType;
  restrictedIds?: number[];
  userGroupIds?: number[];
};

export enum CustomReportVisibilityScopeType {
  Everyone = 'EVERYONE',
  Self = 'SELF',
  Members = 'MEMBERS',
  Group = 'GROUP',
}

export type CustomReportVisibilityPayload = {
  scopeType: CustomReportVisibilityScopeType;
  restrictedIds?: number[];
  userGroupIds?: number[];
};

export interface CustomReportPayload {
  reportType: ReportType;
  sourceType: ReportingSourceType;
  reportName: string;
  reportDescription?: string;
  highlightingRules: {
    rules: HighlightingRule[];
  };
  defaultViewState?: {
    filter?: GroupedFilter | SimpleSearchFilter;
    timeRange: DateRange | null;
    groupBy?: string;
  };
  widgets: (PreviewWidgetPayload | Widget)[];
  columns: ColumnType;
  rows?: ColumnType;
  values?: ColumnType;
  sortModel?: SortModel | null;
  visibility?: CustomReportVisibilityPayload;
}

export interface CustomReport extends CustomReportPayload {
  customReportId: number;
  isDeleted?: boolean;
  updatedBy?: number;
  updatedAt?: Timestamp;
  viewId?: number;
  scheduledExportPresent?: boolean;
}

export interface PivotFields {
  [key: string]: FieldCustomType;
}

export type NumberFieldsMap = PivotFields;

export interface PivotChild {
  headerName: string;
  colId: string;
  field: string;
}

export interface PivotFieldsDefinition {
  groupId: string;
  headerName: string;
  colId: string;
  field: string;
  children: PivotChild[];
}

export const ColorPallete = [
  { name: 'Green50', value: 'var(--green-50)' },
  {
    name: 'Green40',
    value: 'var(--green-40)',
  },
  {
    name: 'GreenSingle40',
    value: 'var(--green-40)',
  },
  {
    name: 'GreenMulti40',
    value: 'var(--green-40)',
  },
  {
    name: 'Green30',
    value: 'var(--green-30)',
  },
  {
    name: 'Green20',
    value: 'var(--green-20)',
  },
  {
    name: 'GreenSingle20',
    value: 'var(--green-20)',
  },
  {
    name: 'GreenMulti20',
    value: 'var(--green-20)',
  },
  { name: 'Yellow50', value: 'var(--yellow-50)' },
  { name: 'YellowSingle50', value: 'var(--yellow-50)' },
  { name: 'YellowMulti50', value: 'var(--yellow-50)' },
  {
    name: 'Yellow40',
    value: 'var(--yellow-40)',
  },
  {
    name: 'Yellow30',
    value: 'var(--yellow-30)',
  },
  {
    name: 'YellowSingle30',
    value: 'var(--yellow-30)',
  },
  {
    name: 'YellowMulti30',
    value: 'var(--yellow-30)',
  },
  {
    name: 'Yellow20',
    value: 'var(--yellow-20)',
  },
  { name: 'Red50', value: 'var(--red-50)' },
  {
    name: 'Red40',
    value: 'var(--red-40)',
  },
  {
    name: 'RedSingle40',
    value: 'var(--red-40)',
  },
  {
    name: 'RedMulti40',
    value: 'var(--red-40)',
  },
  {
    name: 'Red30',
    value: 'var(--red-30)',
  },
  {
    name: 'Red20',
    value: 'var(--red-20)',
  },
  {
    name: 'RedSingle20',
    value: 'var(--red-20)',
  },
  {
    name: 'RedMulti20',
    value: 'var(--red-20)',
  },
];

export const MultiColorPallete = [
  {
    name: 'GreenMulti40',
    value: 'var(--green-40)',
  },
  {
    name: 'GreenMulti20',
    value: 'var(--green-20)',
  },
  {
    name: 'YellowMulti30',
    value: 'var(--yellow-30)',
  },
  {
    name: 'YellowMulti50',
    value: 'var(--yellow-50)',
  },
  {
    name: 'RedMulti20',
    value: 'var(--red-20)',
  },
  {
    name: 'RedMulti40',
    value: 'var(--red-40)',
  },
];

export const SingleColorPalletes = [
  [
    {
      name: 'Green20',
      value: 'var(--green-20)',
    },
    {
      name: 'Green30',
      value: 'var(--green-30)',
    },
    {
      name: 'Green40',
      value: 'var(--green-40)',
    },
    { name: 'Green50', value: 'var(--green-50)' },
  ],
  [
    {
      name: 'Yellow20',
      value: 'var(--yellow-20)',
    },
    {
      name: 'Yellow30',
      value: 'var(--yellow-30)',
    },
    {
      name: 'Yellow40',
      value: 'var(--yellow-40)',
    },
    { name: 'Yellow50', value: 'var(--yellow-50)' },
  ],
  [
    {
      name: 'Red20',
      value: 'var(--red-20)',
    },
    {
      name: 'Red30',
      value: 'var(--red-30)',
    },
    {
      name: 'Red40',
      value: 'var(--red-40)',
    },
    { name: 'Red50', value: 'var(--red-50)' },
  ],
];

export enum HighlightingType {
  SingleColor = 'SINGLE_COLOR',
  MultiColor = 'MULTI_COLOR',
}

export enum HighlightingSingleColorConditionType {
  GreaterThan = 'GREATER_THAN',
  Equals = 'EQUALS',
  LesserThan = 'LESSER_THAN',
}

export enum HighlightingMultiColorType {
  Smart = 'SMART',
  Custom = 'CUSTOM',
}

export interface HighlightingRuleBase {
  fieldName: string;
  fieldLabel: string;
  ruleId: number;
}

interface PaletteType {
  name: string;
  value: string;
}

export interface HighlightingRuleMultiColor extends HighlightingRuleBase {
  multiColorType: HighlightingMultiColorType;
  max: number;
  min: number;
  paletteType: PaletteType[];
  highlightingType: HighlightingType.MultiColor;
}

export interface HighlightingRuleSingleColor extends HighlightingRuleBase {
  conditionType: HighlightingSingleColorConditionType;
  threshold: number;
  color: string;
  highlightingType: HighlightingType.SingleColor;
}

export type HighlightingRule =
  | HighlightingRuleSingleColor
  | HighlightingRuleMultiColor;

export interface RangeAndColor {
  max: number;
  min: number;
  color: string;
}

export interface ResponseType {
  count: number;
  data: RowDataType[];
  excludedFilters?: SimpleSearchFilter[];
}

export enum WidgetType {
  CustomCard = 'CUSTOM_CARD',
  CustomChart = 'CUSTOM_CHART',
  CustomTable = 'CUSTOM_TABLE',
}

export enum WidgetTypes {
  Resolve = 'RESOLVE',
  Preview = 'PREVIEW',
}

export enum SortType {
  Default = '',
  Asc = 'ASC',
  Desc = 'DESC',
}

export interface TimeRangeFilter {
  startDateFilter?: string;
  endDateFilter?: string;
}

export interface CustomField {
  fieldId: number;
  fieldClass: FieldClass.CustomField;
  fieldSource?: string;
  aggType?: string;
  name: string;
  disableTotal?: boolean;
  disableAvg?: boolean;
  fieldType?: FieldOptionType;
}

export interface NativeAndDefaultField {
  name: string;
  fieldClass: FieldClass.NativeField;
  fieldSource?: string;
  aggType?: string;
  disableTotal?: boolean;
  disableAvg?: boolean;
  fieldType?: FieldOptionType;
}

export type Field = NativeAndDefaultField | CustomField;

export interface CardMetaFields {
  unitName?: string;
  unitType: UnitPosition;
  aggType: AggregateType;
  field: Field;
  startDateFilter?: string;
  endDateFilter?: string;
}

export interface PreviewCardWidgetPayload {
  simpleSearchFilter: SimpleSearchFilter;
  globalDashboardFilter?: SimpleSearchFilter;
  widgetType: WidgetType.CustomCard;
  widgetName: string;
  metaData: CardMetaFields;
  type?: WidgetTypes.Preview;
  widgetId?: number;
  widgetEdited?: boolean;
  sortModel?: SortModel | null;
}

export interface Axis {
  title: string;
  field: Field;
  name?: number;
  opposite?: boolean;
}
export interface FieldOption {
  name?: number;
  opposite?: boolean;
  label: string;
  value: Field;
}

export interface ChartNumberFieldOptions {
  label: string;
  options: FieldOption[];
}

export interface ChartMetaFields {
  chart: {
    chartType: string;
  };
  aggType: AggregateType;
  xAxis: Axis;
  yAxes: Axis[];
  groupBy?: Field;
  zoomLevel?: ZoomLevel;
  sortOrder?: SortType;
  startDateFilter?: string;
  endDateFilter?: string;
  stackingType?: ChartStackingType;
}

export interface PreviewChartWidgetPayload {
  simpleSearchFilter: SimpleSearchFilter;
  globalDashboardFilter?: SimpleSearchFilter;
  widgetType: WidgetType.CustomChart;
  widgetObjectType: string;
  metaData: ChartMetaFields;
  widgetName: string;
  type?: WidgetTypes.Preview;
  widgetId?: number;
  widgetEdited?: boolean;
  sortModel?: SortModel | null;
}

export type PreviewWidgetPayload =
  | PreviewCardWidgetPayload
  | PreviewChartWidgetPayload;

export interface ChartFilter {
  projectsFilter: GroupedFilter;
  tasksFilter: GroupedFilter;
}

export type CustomChartFormValues = {
  widgetName: string;
  unitName?: string;
  objectType: string;
  chartType: {
    label: string;
    value: string;
  };
  aggType: AggregateType;
  xAxis: FieldOption;
  yAxes: FieldOption[];
  groupBy?: FieldOption;
  zoomLevel?: ZoomLevel;
};

export const NumberWidgetTypes = [WidgetType.CustomCard];

export enum WidgetDefaultNumberFields {
  ProjectCount = 'projectCount',
  CustomerCount = 'companyCount',
  CSAT = 'csat',
  TaskCount = 'taskCount',
  CsatRating = 'csatRating',
  UserCount = 'userCount',
  BudgetCount = 'budgetCount',
}

export enum WidgetNativeNumberFields {
  ARR = 'annualizedRecurringRevenue',
  ProjectFee = 'projectFee',
}

export enum WidgetNativeValues {
  ARR = 'annualizedRecurringRevenue',
  ProjectFee = 'projectFee',
  ProjectBudget = 'projectBudget',
  Effort = 'effort',
  CostRate = 'costRate',
  TrackedMinutes = 'trackedMinutes',
  StartDate = 'startDate',
  CreatedDate = 'date',
  UserStaus = 'userStatus',
  UsedByPartners = 'usedByPartners',
  CapacityMinutes = 'capacityMinutes',
  CompanyName = 'companyName',
}

export enum UnitPosition {
  Prefix = 'PREFIX',
  Suffix = 'SUFFIX',
}

export enum FieldUnit {
  Hours = 'HOURS',
  Currency = 'CURRENCY',
  Percentage = 'PERCENTAGE',
  Number = 'NUMBER',
}

export enum VerticalAxisDefaultTaskFields {
  TaskCount = 'count',
  TaskCSAT = 'csat',
  TaskTrackedMinutes = 'trackedMinutes',
  TaskEffort = 'effort',
  TaskProgress = 'progress',
}

export enum VerticalAxisDefaultProjectFields {
  ProjectCount = 'count',
  CSAT = 'csat',
  ProjectBugetedHours = 'budgetedHours',
  ProjectTrackedMinutes = 'trackedMinutes',
  ProjectAge = 'projectAge',
}

export enum HorizontalAxisDefaultTaskFields {
  TaskStartDate = 'startDate',
  TaskDueDate = 'dueDate',
  TaskActualStartDate = 'actualStartDate',
  TaskActualDueDate = 'actualDueDate',
  TaskStatus = 'status',
  TaskAssignee = 'taskAssignee',
}

export enum HorizontalAxisDefaultProjectFields {
  ProjectStatus = 'status',
  Templates = 'template',
  ProjectCreatedDate = 'createdDate',
  ProjectStartDate = 'startDate',
  ProjectDueDate = 'dueDate',
  ActualProjectStartDate = 'actualStartDate',
  ActualProjectCompletionDate = 'actualDueDate',
  ProjectOwner = 'projectOwner',
}

export enum ChartStackingType {
  Normal = 'normal',
  Percent = 'percent',
}

export const NativeDateFields = [
  HorizontalAxisDefaultProjectFields.ProjectCreatedDate,
  HorizontalAxisDefaultProjectFields.ProjectStartDate,
  HorizontalAxisDefaultProjectFields.ProjectDueDate,
  HorizontalAxisDefaultProjectFields.ActualProjectStartDate,
  HorizontalAxisDefaultProjectFields.ActualProjectCompletionDate,
  HorizontalAxisDefaultTaskFields.TaskDueDate,
  HorizontalAxisDefaultTaskFields.TaskStartDate,
  HorizontalAxisDefaultTaskFields.TaskActualDueDate,
  HorizontalAxisDefaultTaskFields.TaskActualStartDate,
];

export const excludedTaskFieldsForCustomChart = ['priority'];

export enum GroupByDefaultProjectFields {
  ProjectOwner = 'projectOwner',
  ProjectStatus = 'status',
}

export enum GroupByDefaultTaskFields {
  TaskStatus = 'status',
}
export interface BaseFieldFilter {
  operation: Operation;
  value?: string;
  sourceType: SourceType;
}

export interface NativeFieldFilter extends BaseFieldFilter {
  name: string;
}

export interface CustomFieldFilter extends BaseFieldFilter {
  fieldId: number;
}

export type SimpleSearchFieldFilter = NativeFieldFilter | CustomFieldFilter;

export interface SimpleFilter {
  nativeFields?: NativeFieldFilter[];
  customFields?: CustomFieldFilter[];
}

export interface SimpleSearchFilter extends SimpleFilter {
  match: QueryCombinerType;
}

export interface EditDefaultWidgetPayload {
  widgetName: string;
  simpleSearchFilter: SimpleSearchFilter;
  excludedFilters?: SimpleSearchFilter[];
}

export interface EditCustomCardWidgetPayload extends EditDefaultWidgetPayload {
  metaData: CardMetaFields;
  metaDataV2: CardMetaFields;
}

interface EditCustomChartWidgetPayload extends EditDefaultWidgetPayload {
  widgetObjectType: string;
  metaData: ChartMetaFields;
  metaDataV2: ChartMetaFields;
}

interface CreateDefaultWidgetPayload extends EditDefaultWidgetPayload {
  widgetType: WidgetType;
  widgetOrder?: number;
}

interface DefaultWidget extends CreateDefaultWidgetPayload {
  widgetId: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  enabled: boolean;
  deleted: boolean;
  widgetOrder: number;
  type?: WidgetTypes.Resolve;
  widgetEdited?: boolean;
  widgetObjectType: string;
}

export interface CreateCustomCardWidgetPayload
  extends CreateDefaultWidgetPayload {
  metaData: CardMetaFields;
  metaDataV2: CardMetaFields;
}

export interface CreateCustomChartWidgetPayload
  extends CreateDefaultWidgetPayload {
  widgetObjectType: string;
  metaData: ChartMetaFields;
  metaDataV2: ChartMetaFields;
}

export interface ColumnMeta {
  columnOrder: ColumnOrder[];
  lastUpdatedBy: number;
  lastUpdatedAt?: number;
}

export interface EditColumnOrderPayload {
  columnMeta: ColumnMeta;
}

export type CreateWidgetPayload =
  | CreateDefaultWidgetPayload
  | CreateCustomCardWidgetPayload
  | CreateCustomChartWidgetPayload;

export type EditWidgetPayload =
  | EditDefaultWidgetPayload
  | EditCustomCardWidgetPayload
  | EditCustomChartWidgetPayload
  | EditColumnOrderPayload;

export type CustomCardWidget = CreateCustomCardWidgetPayload & DefaultWidget;
export type CustomChartWidget = CreateCustomChartWidgetPayload & DefaultWidget;
export type CustomTableWidget = CreateCustomTableWidgetPayload & DefaultWidget;

export type Widget = CustomCardWidget | CustomChartWidget | CustomTableWidget;

export interface Xaxis extends XAxisOptions {
  categories: string[];
  categoryDrillDowns?: SimpleFilter[];
  categoryIds?: number[];
  field?: Field;
}

export interface Yaxis extends YAxisOptions {
  field?: Field;
}

interface SeriesChart extends SeriesColumnOptions {
  pointData: PointOptionsObject[];
}

interface ChartType extends ChartOptionsValue {
  chartType?: string;
}

export interface ChartOptions extends Options {
  chart?: ChartType;
  series?: SeriesChart[];
  xAxis?: Xaxis;
  yAxis?: Yaxis[];
  yAxes: Yaxis[];
  groupBy?: Field;
  showWidget?: boolean;
  excludedFilters?: SimpleSearchFilter[];
  accountCurrency?: AccountCurrency;
}

export interface WidgetPayload {
  widgetIds: number[];
  simpleSearchFilter: SimpleSearchFilter;
  sortOrder?: SortType;
  timeRangeFilter?: TimeRangeFilter;
}

export interface WidgetResponse {
  widgetId: number;
  widgetType: WidgetType;
  widgetObjectType: string;
  response: ChartOptions;
  reportResponse?: ChartOptions;
  reportResponseStatus: 'SUCCESS' | 'ERRORED';
}
export interface WidgetProps {
  widget: Widget;
  isDragging?: boolean;
}

export interface WidgetOrder {
  widgetId: number;
  widgetOrder: number;
}

export interface DrillDownPayload {
  drillDownFilter?: SimpleFilter;
  dashboardFilter?: SimpleSearchFilter;
  columnOrder?: string[];
}

export enum DrillDownExportType {
  PROJECT = 'PROJECT',
  TASK = 'TASK',
}

export type DrillDownExportResponse =
  | {
      exportMetaData: ExportMetaData;
      drillDownExportType: DrillDownExportType.PROJECT;
    }
  | {
      asyncProcessResponse: ExportResponse;
      drillDownExportType: DrillDownExportType.TASK;
    };
